import { Settings } from 'lucide-react'
import type { Parametres } from '../types'

interface Props { parametres: Parametres; onChange: (p: Parametres) => void; configStep: number }

const e0 = (n: number) => new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(n)

export default function SectionParametres({ parametres: p, onChange, configStep }: Props) {
  const set = <K extends keyof Parametres>(k: K, v: Parametres[K]) => onChange({ ...p, [k]: v })
  const pct = Math.round((configStep / 3) * 100)

  return (
    <section className="rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 flex items-center justify-between" style={{ background: '#F8FAFC' }}>
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-gray-500" />
          <span className="font-semibold text-sm text-gray-700">Paramètres de Calcul</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>Config : {configStep}/3</span>
          <div className="w-16 h-1.5 rounded-full bg-gray-200">
            <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: '#1A3C6E' }}/>
          </div>
          <span className="font-semibold" style={{ color: '#1A3C6E' }}>{pct}%</span>
        </div>
      </div>

      <div className="bg-white p-5 space-y-5">

        {/* Taux remplissage */}
        <div>
          <div className="flex justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">Taux de remplissage agenda</label>
            <span className="font-bold text-sm" style={{ color: '#1A3C6E' }}>{p.tauxRemplissage}%</span>
          </div>
          <input type="range" min={10} max={100} step={5} value={p.tauxRemplissage}
            onChange={e => set('tauxRemplissage', +e.target.value)} className="w-full accent-blue-700"/>
          <p className="text-xs text-gray-400 mt-0.5">
            {p.tauxRemplissage < 65 ? <span className="text-red-500 font-medium">⚠ Trop faible — objectif min. 70%</span>
              : p.tauxRemplissage >= 85 ? <span className="text-green-600">✅ Excellent</span>
              : <span>Correct</span>}
          </p>
        </div>

        {/* TVA */}
        <div className="rounded-xl p-4" style={{background:'#F0FDF4',border:'1px solid #BBF7D0'}}>
          <div className="font-semibold text-sm mb-3" style={{color:'#166534'}}>🧾 TVA — Conversion HT → TTC</div>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 mb-1">Taux de TVA applicable</label>
              <div className="relative">
                <input type="number" min={0} max={30} step={0.5}
                  value={p.tauxTVA}
                  onChange={e => set('tauxTVA', parseFloat(e.target.value)||0)}
                  className="w-full pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-200 font-bold text-lg"/>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-500">%</span>
              </div>
            </div>
            <div className="flex-1 text-xs rounded-lg p-2.5" style={{background:'#DCFCE7',color:'#166534'}}>
              <div>Seuil HT <strong>× {(1+p.tauxTVA/100).toFixed(2)}</strong> = Seuil TTC</div>
              <div className="mt-1 opacity-70">
                TVA 20% : prestations coiffure standard<br/>
                TVA 10% : certains services<br/>
                Saisissez votre taux réel
              </div>
            </div>
          </div>
        </div>

        <hr className="border-gray-100"/>

        {/* BLOC PRODUITS */}
        <div className="rounded-xl p-4 space-y-4" style={{ background: '#F0F7FF', border: '1px solid #DBEAFE' }}>
          <div className="font-semibold text-sm" style={{ color: '#1D4ED8' }}>🧴 Produits</div>

          {/* Ligne 1 : Produits techniques UTILISÉS */}
          <div>
            <div className="flex justify-between mb-1">
              <div>
                <label className="text-sm font-medium text-gray-700">% Produits techniques utilisés</label>
                <p className="text-xs text-gray-500">Consommables prestations — <strong className="text-red-600">↑ augmente le seuil</strong></p>
              </div>
              <span className="font-bold text-sm" style={{ color: '#C62828' }}>{p.pctProduitsTechUtilises}%</span>
            </div>
            <input type="range" min={0} max={25} step={1} value={p.pctProduitsTechUtilises}
              onChange={e => set('pctProduitsTechUtilises', +e.target.value)} className="w-full accent-red-600"/>
            <p className="text-xs text-gray-400 mt-0.5">Ex : shampoings, colorations… utilisés en prestation</p>
          </div>

        </div>

        {/* Grille paramètres numériques */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Jours travaillés / mois</label>
            <div className="relative">
              <input type="number" min={18} max={26} value={p.joursTravailles}
                onChange={e => set('joursTravailles', +e.target.value || 22)}
                className="w-full pl-3 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"/>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">j</span>
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Objectif bénéfice mensuel avant impôts
            </label>
            <div className="relative">
              <input type="number" min={0} value={p.objectifBenefice || ''}
                onChange={e => set('objectifBenefice', +e.target.value || 0)}
                placeholder="0"
                className="w-full pl-3 pr-20 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"/>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">€ HT/mois</span>
            </div>
            {p.objectifBenefice === 0 && (
              <p className="text-xs mt-1" style={{ color: '#E65100' }}>
                ⚠ Objectif à 0 € — le seuil ne tient pas compte de la rentabilité souhaitée.
              </p>
            )}
          </div>
        </div>

        {/* Seuil sans gérant */}
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={p.afficherSeuilSansGerant}
            onChange={e => set('afficherSeuilSansGerant', e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-blue-700"/>
          <div>
            <span className="text-sm font-medium text-gray-700">Afficher le seuil sans gérant</span>
            <p className="text-xs text-gray-400">Calcule un 2e seuil en excluant le coût total du dirigeant TNS</p>
          </div>
        </label>

        {/* Cadrage HT/TTC */}
        <div className="rounded-xl p-3 text-xs" style={{ background: '#F0F7FF', border: '1px solid #DBEAFE', color: '#1E40AF' }}>
          <strong>📌 Cadrage financier</strong>
          <div className="mt-1 space-y-0.5 text-blue-700/80">
            <div>• Charges fixes &amp; salaires → <strong>HT</strong> (bilan comptable)</div>
            <div>• Objectif bénéfice → <strong>HT avant impôts</strong></div>
            <div>• CA minimum &amp; coût-minute → <strong>TTC</strong> (encaissé réel)</div>
          </div>
        </div>
      </div>
    </section>
  )
}
